import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_OPTIONS = [
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
];

export default function BlockerInbox() {
  const { user } = useAuth();
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(`/blockers`, { params: { status } });
      setRows(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load blockers");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  if (!user?.is_platform_admin) {
    return (
      <div data-testid="blocker-inbox-root" className="max-w-3xl">
        <h1 className="font-serif text-4xl tracking-tight">Blocker Inbox</h1>
        <p className="text-sm text-[#B5462F] mt-4">Platform admin only.</p>
      </div>
    );
  }

  return (
    <div data-testid="blocker-inbox-root" className="max-w-5xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#6B6358]">Brand admin queue</p>
      <h1 className="font-serif text-4xl tracking-tight mt-1">Blocker Inbox</h1>
      <p className="text-sm text-[#3D3A35] mt-3 max-w-2xl">
        Slots a marketer paused while waiting on missing brand assets. Resolve
        each blocker once the source content is available in the Brand Kit
        or Content Library; the marketer is notified when the last open
        blocker on their document is closed.
      </p>

      <div className="mt-6 flex gap-2">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            data-testid={`blocker-inbox-filter-${opt.id}`}
            onClick={() => setStatus(opt.id)}
            className={[
              "px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border",
              status === opt.id
                ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                : "bg-white text-[#3D3A35] border-[#1A1F2C]/30",
            ].join(" ")}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-[#B5462F] mt-4">{error}</p>}

      {rows == null ? (
        <p className="text-[#6B6358] mt-6">Loading…</p>
      ) : rows.length === 0 ? (
        <div data-testid="blocker-inbox-empty" className="mt-8 border border-dashed border-[#1A1F2C]/20 rounded-lg p-10 text-center">
          <p className="text-[#6B6358]">
            {status === "open" ? "No open blockers. 🎉" : "No resolved blockers yet."}
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-3">
          {rows.map((b) => (
            <BlockerRow
              key={b.blocker_id}
              blocker={b}
              open={openId === b.blocker_id}
              onToggle={() => setOpenId(openId === b.blocker_id ? null : b.blocker_id)}
              onResolved={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BlockerRow({ blocker, open, onToggle, onResolved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const resolve = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/blockers/${blocker.blocker_id}/resolve`, {
        resolution_note: note.trim() || null,
      });
      onResolved?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not resolve");
    } finally {
      setBusy(false);
    }
  };

  const isOpen = blocker.status === "open";
  const accent = isOpen ? "#C28A2E" : "#1F6B6B";

  return (
    <div
      data-testid={`blocker-row-${blocker.blocker_id}`}
      className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5"
    >
      <div className="flex flex-wrap gap-3 items-baseline justify-between">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${accent}1a`, color: accent }}
            >
              {blocker.status}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">
              {blocker.kind} · {blocker.panel_id} / {blocker.slot_id}
            </span>
          </div>
          <h3 className="text-base font-medium text-[#1A1F2C] mt-1">
            {blocker.dreq_name || blocker.slot_label || blocker.slot_id}
            {blocker.subject_hint && (
              <span className="text-sm font-normal text-[#6B6358]"> · {blocker.subject_hint}</span>
            )}
          </h3>
          <p className="text-xs text-[#6B6358] mt-1">
            Slot <strong>{blocker.slot_label || blocker.slot_id}</strong> · From{" "}
            <strong>{blocker.requested_by_name || blocker.requested_by_email}</strong>
            {blocker.dreq_brief && (<> · notes: <em className="text-[#3D3A35]">&ldquo;{blocker.dreq_brief}&rdquo;</em></>)}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Link
            data-testid={`blocker-row-${blocker.blocker_id}-open`}
            to={`/brands/${blocker.brand_id}/design-requests/${blocker.design_request_id}`}
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/30 rounded-full text-[#3D3A35]"
          >
            Open dreq
          </Link>
          <button
            data-testid={`blocker-row-${blocker.blocker_id}-toggle`}
            onClick={onToggle}
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/30 rounded-full text-[#3D3A35]"
          >
            {open ? "Hide" : "Details"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#3D3A35]">
          {blocker.what_the_ai_tried && (
            <Field label="What the AI tried">{blocker.what_the_ai_tried}</Field>
          )}
          {blocker.marketer_note && (
            <Field label="Marketer note">{blocker.marketer_note}</Field>
          )}
          {blocker.campaign && <Field label="Campaign">{blocker.campaign}</Field>}
          <Field label="Brand">{blocker.brand_id}</Field>
          <Field label="Design request">{blocker.design_request_id}</Field>
          <Field label="Created">{new Date(blocker.created_at).toLocaleString()}</Field>
          {blocker.applicable_rule_ids?.length > 0 && (
            <Field label="Applicable rules">{blocker.applicable_rule_ids.join(", ")}</Field>
          )}

          {isOpen && (
            <div className="md:col-span-2 mt-2 border-t border-[#1A1F2C]/10 pt-4">
              <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-1">
                Resolution note (optional)
              </p>
              <textarea
                data-testid={`blocker-row-${blocker.blocker_id}-note`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="e.g. Uploaded 3 beach photos to the Brand Photography Library."
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white"
              />
              {error && <p className="text-sm text-[#B5462F] mt-2">{error}</p>}
              <div className="mt-3">
                <button
                  data-testid={`blocker-row-${blocker.blocker_id}-resolve`}
                  disabled={busy}
                  onClick={resolve}
                  className="px-5 py-2.5 bg-[#1F6B6B] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-60"
                >
                  {busy ? "Resolving…" : "Mark resolved"}
                </button>
              </div>
            </div>
          )}

          {!isOpen && blocker.resolution_note && (
            <div className="md:col-span-2 border-t border-[#1A1F2C]/10 pt-4">
              <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-1">
                Resolution note
              </p>
              <p className="text-sm text-[#3D3A35]">{blocker.resolution_note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">{label}</div>
      <div className="text-sm text-[#3D3A35] mt-0.5 whitespace-pre-wrap">{children}</div>
    </div>
  );
}
